import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { TypologyPerformanceResponse } from '../../services/benchmarkService';
import { MetricInfo, NotComputed, PanelError, PanelSkeleton, SectionCard } from './BenchmarkPrimitives';
import { DEFINITIONS, count, decimal, hasValue, percent, scenarioName } from './BenchmarkVocabulary';
import { QualifiedBadge, WithheldNotice } from './BenchmarkQualifiers';
import { NO_WITHHELD, PANEL_FAMILIES, WithheldIndex, qualificationsFor, suppressionReasons } from './withheldMetrics';
import { dsVar, ink } from '../../theme/tokens';

interface ScenarioPerformanceProps {
    performance: TypologyPerformanceResponse | null;
    withheld?: WithheldIndex;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
}

/**
 * Which detection scenarios the agent helps with, and which it does not.
 *
 * Led by the hit rate in the tiers the agent marked worth working — the figure an
 * investigator recognises. The AUC is still here, as secondary text, because a
 * scenario can have a strong ranking and a weak hit rate when it is rare, and
 * hiding it entirely would make the two tabs disagree.
 *
 * Split out of "Typology Consistency & Temporal Drift Audit", whose stability
 * half now sits on the Evidence tab where model validators look for it.
 */
const ScenarioPerformance: React.FC<ScenarioPerformanceProps> = ({
    performance, withheld = NO_WITHHELD, loading = false, error, onRetry
}) => {
    if (error) {
        return (
            <SectionCard title="Which scenarios improve" icon={<CategoryOutlinedIcon fontSize="small" />}>
                <PanelError message={error} onRetry={onRetry} />
            </SectionCard>
        );
    }
    if (loading) return <PanelSkeleton height={340} />;
    if (!performance) return null;

    const scenarios = [...performance.typologies].sort((a, b) => {
        const av = hasValue(a.elevatedHitRate) ? a.elevatedHitRate : -1;
        const bv = hasValue(b.elevatedHitRate) ? b.elevatedHitRate : -1;
        return (bv as number) - (av as number);
    });

    return (
        <SectionCard
            title="Which scenarios improve"
            subtitle="How the agent performs on each kind of alert your monitoring raises."
            icon={<CategoryOutlinedIcon fontSize="small" />}
            accent="#A78BFA"
            badges={<QualifiedBadge qualifications={qualificationsFor(withheld, PANEL_FAMILIES.scenarios)} />}
        >
            {suppressionReasons(withheld, PANEL_FAMILIES.scenarios).length > 0 ? (
                <WithheldNotice reasons={suppressionReasons(withheld, PANEL_FAMILIES.scenarios)} />
            ) : scenarios.length === 0 ? (
                <NotComputed detail="This run recorded no scenario breakdown." />
            ) : (
                <Box className="flex flex-col gap-4">
                    {scenarios.map((scenario) => (
                        <Box key={scenario.segmentCode} className="rounded-xl bg-page/50 border border-line p-4">
                            <Box className="flex flex-wrap items-baseline justify-between gap-2">
                                <Box>
                                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: dsVar('fg-body') }}>
                                        {scenarioName(scenario.segmentCode)}
                                    </Typography>
                                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                        {scenario.segmentCode}
                                    </Typography>
                                </Box>
                                <Box className="text-right">
                                    <Box className="flex items-center gap-1.5 justify-end">
                                        <Typography sx={{ fontSize: '1.125rem', fontWeight: 800, color: ink('#10B981') }}>
                                            {percent(scenario.elevatedHitRate)}
                                        </Typography>
                                        <MetricInfo definition={DEFINITIONS.urgentHitRate} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                        confirmed, among alerts the agent prioritised
                                    </Typography>
                                </Box>
                            </Box>

                            {hasValue(scenario.elevatedHitRate) && (
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, (scenario.elevatedHitRate as number) * 100)}
                                    sx={{
                                        mt: 1.5, height: 6, borderRadius: 3, backgroundColor: dsVar('line'),
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#10B981', borderRadius: 3 }
                                    }}
                                />
                            )}

                            <Box className="flex flex-wrap gap-x-6 gap-y-1 mt-3">
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                    {count(scenario.alertCount ?? scenario.sampleSize)} alerts
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                    {count(scenario.confirmedCount)} confirmed
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                    {count(scenario.sarCount)} SARs filed
                                </Typography>
                                {/*
                                  Secondary, and labelled with its technical name so a
                                  validator and an operations lead can refer to the same
                                  figure without translating between them.
                                */}
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-faint') }}>
                                    Ranking quality (ROC AUC) {decimal(scenario.agentRocAuc)}
                                    {hasValue(scenario.baselineRocAuc) && ` vs ${decimal(scenario.baselineRocAuc)} today`}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </SectionCard>
    );
};

export default ScenarioPerformance;
